import { createTheme, Loader, rem } from '@mantine/core';
import HuginnLoader from './components/HuginnLoader';

/**
 * RiskManager theme — the token layer.
 *
 * `docs/wireframes/wireframes.html` is the visual spec, and its 33 CSS custom properties are the
 * product's design tokens. This file is where they live once, so no screen invents a hex.
 *
 * Three rules the palette encodes:
 *
 *   1. **`--bar` is not `--navy`.** #26405E is the chrome — the nav bar and its hover state.
 *      #14243C is text. Using navy for the bar makes the shell read as a black slab; using bar
 *      for text makes body copy look washed. They are different jobs.
 *   2. **Amber and red are reserved for severity and status.** Nothing decorative may use them.
 *      That is why there is no orange or yellow key in the palette at all. Severity hexes live
 *      in utils/severityColors.js, keyed to the 8-band findings.severity constraint.
 *   3. **The owner has their own colour.** `--own` #2C4A6B marks first-line surfaces. The
 *      product is two products sharing a database (BRIEF.md §7), and the deck expresses that
 *      split in colour before it expresses it in navigation.
 *
 * Mantine needs exactly 10 shades per colour and picks `primaryShade` as the solid one. The
 * brand values sit deep in each scale — #14243C is very dark, which is what makes it a navy
 * rather than a blue — so primaryShade is 8, not the default 6.
 *
 * Raw deck values are also emitted as `--rm-*` CSS variables by cssVariablesResolver below, so
 * a component can reach a token directly when a Mantine colour prop would be the wrong shape.
 */

const navy = [
  '#f4f6f9', '#e6eaf1', '#c8d2e0', '#a6b6cd', '#899cb9',
  '#6d80a0', '#4e6486', '#33486a',
  '#1f3352', // solid — buttons, filled elements
  '#14243c', // --navy · text, deepest surface
];

// The chrome. Kept as its own scale so hover has somewhere lighter to go, but its solid shade
// is now navy[8] — chrome and primary action are one colour (TOKENS.bar).
const bar = [
  '#f2f5f8', '#e3e9ef', '#c5d0dc', '#a3b4c6', '#8299b1',
  '#6a839f', '#4f6a89', '#3b5878',
  '#2f4c6e', // hover
  '#1f3352', // chrome — same as navy[8]
];

const teal = [
  '#eff7f4', '#d8ebe4', '#b1d7c9', '#85c2ac', '#60b094',
  '#45a485', '#369c7c', '#25886a',
  '#1a7a5f', // solid — primary actions
  '#0f6e56', // --teal
];

// Cool greys, blue-leaning rather than the warm default, so surfaces read Scandinavian.
const slate = [
  '#f7f8fa', '#eef0f4', '#dfe3e9', '#cbd2db', '#b3bcc9',
  '#96a1b2', '#78859a', '#5c6a7e', '#414d5e', '#2b3441',
];

// First line. Used on owner surfaces only — never as a general accent.
const own = [
  '#f2f6fa', '#eaf0f6', '#cfdce8', '#aec3d8', '#8aa7c5',
  '#6b8db3', '#52759c', '#3e6086',
  '#355574', // solid
  '#2c4a6b', // --own
];

/** The deck's tokens verbatim. Emitted as CSS variables; also the reference for the scales. */
export const TOKENS = {
  navy: '#14243C',
  // #1F3352, not the deck's #26405E. This is navy[8] — the colour an unstyled Button already
  // uses, so the chrome and the primary action are the same value rather than two navies a few
  // degrees apart. Founder call, 2026-08-03: deeper, and it stops the bar and the buttons
  // looking like a near-miss.
  bar: '#1F3352',
  barHover: '#2F4C6E',
  navy70: '#4A5768',
  navy40: '#8A93A0',
  navy20: '#C3C9D1',

  teal: '#0F6E56',
  tealSoft: '#E6F0ED',
  tealLine: '#B9D5CC',

  bg: '#F4F5F7',
  surface: '#FFFFFF',
  side: '#EEF0F2',
  line: '#E0E3E7',
  lineSoft: '#EDEFF1',
  greySoft: '#F1F2F4',

  // Status. Reserved — see rule 2.
  red: '#B94D4D',
  redSoft: '#FBEFEF',
  redLine: '#EBD3D3',
  amber: '#B07D3A',
  amberSoft: '#FBF4E9',
  amberLine: '#EADFC9',

  own: '#2C4A6B',
  ownSoft: '#EAF0F6',
  ownLine: '#CFDCE8',

  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
};

/**
 * Emit every token as a `--rm-*` variable.
 *
 * The deck styles with raw custom properties, and several things a screen needs — a 2px left
 * rule, a soft status background, the monospace stack for a ref — are not Mantine colour props.
 * Mirroring them here means a component never hard-codes a hex, and a token change lands
 * everywhere at once.
 */
export const cssVariablesResolver = () => ({
  variables: {
    '--rm-navy': TOKENS.navy,
    '--rm-bar': TOKENS.bar,
    '--rm-bar-hover': TOKENS.barHover,
    '--rm-navy-70': TOKENS.navy70,
    '--rm-navy-40': TOKENS.navy40,
    '--rm-navy-20': TOKENS.navy20,
    '--rm-teal': TOKENS.teal,
    '--rm-teal-soft': TOKENS.tealSoft,
    '--rm-teal-line': TOKENS.tealLine,
    '--rm-bg': TOKENS.bg,
    '--rm-surface': TOKENS.surface,
    '--rm-side': TOKENS.side,
    '--rm-line': TOKENS.line,
    '--rm-line-soft': TOKENS.lineSoft,
    '--rm-grey-soft': TOKENS.greySoft,
    '--rm-red': TOKENS.red,
    '--rm-red-soft': TOKENS.redSoft,
    '--rm-red-line': TOKENS.redLine,
    '--rm-amber': TOKENS.amber,
    '--rm-amber-soft': TOKENS.amberSoft,
    '--rm-amber-line': TOKENS.amberLine,
    '--rm-own': TOKENS.own,
    '--rm-own-soft': TOKENS.ownSoft,
    '--rm-own-line': TOKENS.ownLine,
    '--rm-mono': TOKENS.mono,
    // One hairline definition, used by every shared primitive.
    '--rm-hairline': `1px solid ${TOKENS.line}`,
    // The height of the area a page gets: the viewport less the 48px bar and the shell's md
    // padding top and bottom. Defined once because two things need to agree on it — the sidebar,
    // which stretches to it, and PageLoader, which centres inside it. They were separately
    // hardcoding `calc(100vh - 48px - 32px)`, which is the kind of number that drifts the moment
    // the bar changes height.
    '--rm-shell-content': 'calc(100vh - 48px - 32px)',
  },
  light: {},
  dark: {},
});

export const theme = createTheme({
  primaryColor: 'navy',
  primaryShade: { light: 8, dark: 7 },

  colors: { navy, bar, teal, slate, own },

  // Hanken Grotesk, loaded in public/index.html — the landing page's typeface, so the product
  // and the site read as one thing. The system stack stays as the fallback so a blocked font
  // request degrades to something reasonable rather than to Times.
  fontFamily:
    '"Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace: TOKENS.mono,

  // The deck runs 9–13.5px body against 19/24/26px headings — considerably denser than
  // Mantine's defaults, which is what makes its tables read as tables rather than as cards.
  fontSizes: {
    xs: rem(11),
    sm: rem(12.5),
    md: rem(13.5),
    lg: rem(15),
    xl: rem(19),
  },
  headings: {
    fontFamily:
      '"Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: rem(26), lineHeight: '1.25' },
      h2: { fontSize: rem(24), lineHeight: '1.3' },
      h3: { fontSize: rem(19), lineHeight: '1.35' },
      h4: { fontSize: rem(15), lineHeight: '1.4' },
      h5: { fontSize: rem(13.5), lineHeight: '1.45' },
    },
  },

  defaultRadius: 'sm',

  // Hairline borders, almost no shadow: depth comes from spacing and rules, not elevation.
  shadows: {
    xs: '0 1px 2px rgba(20, 36, 60, 0.04)',
    sm: '0 1px 3px rgba(20, 36, 60, 0.06)',
    md: '0 2px 8px rgba(20, 36, 60, 0.08)',
  },

  other: {
    ...TOKENS,
    hairline: 'var(--rm-hairline)',
    surface: 'var(--rm-surface)',
    accent: 'teal',
  },

  components: {
    /**
     * The product has one loading indicator: the pounding mark. Registering it as Mantine's
     * default loader type is what makes that true everywhere at once — `<Loader />`,
     * `<Button loading>`, `<ActionIcon loading>` and `<LoadingOverlay />` all resolve through
     * here, so a CTA the user clicks beats inside its own button and no screen has to remember
     * to opt in. The stock oval stays reachable as `type="oval"`; nothing should need it.
     */
    Loader: {
      defaultProps: {
        loaders: { ...Loader.defaultLoaders, huginn: HuginnLoader },
        type: 'huginn',
      },
    },

    Button: { defaultProps: { radius: 'sm' } },
    Badge: { defaultProps: { radius: 'sm', variant: 'light' } },
    Table: { defaultProps: { horizontalSpacing: 'md', verticalSpacing: 'sm' } },
    Paper: { defaultProps: { radius: 'sm', withBorder: true, shadow: 'none' } },
  },
});

export default theme;
